import { useGlobalSearchParams } from "expo-router";
import type { PropsWithChildren } from "react";
import { useEffect, useRef } from "react";
import { Animated, Easing, Platform, StyleSheet, View } from "react-native";
import {
  initialWindowMetrics,
  SafeAreaProvider,
  useSafeAreaInsets
} from "react-native-safe-area-context";

import { AuthProvider } from "@/auth/AuthProvider";
import { AccountProvider } from "@/data/AccountProvider";
import { CatalogProvider } from "@/data/CatalogProvider";
import { DemoModeProvider } from "@/demo/DemoModeProvider";
import { space } from "@/design/tokens";
import { PlaybackSessionProvider } from "@/playback/PlaybackSession";
import { AirPlayControllerProvider } from "@/playback/airplay/AirPlayController";
import { CastControllerProvider } from "@/playback/cast/CastController";
import { CastMiniController } from "@/playback/cast/CastMiniController";
import { CastMiniPill } from "@/playback/cast/CastMiniPill";
import { getCastMiniBottomOffset } from "@/playback/cast/castMiniLayout";
import { ReadinessProvider } from "@/providers/ReadinessGate";

// Dev-mock artwork as a data URI: react-native-web's Image cannot use
// resolveAssetSource or SVG data URIs, and the preview must never risk the
// production bundle. Base64 PNG: four brand-toned vertical bars, 72x50.
const previewArtworkUrl =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEgAAAAyCAIAAABwNgq3AAAAX0lEQVR4nO3PMQ3AIAAAMHTtRAYS+LkxgAOEYAMpyxRggRHOJjXQUNt7YPRvU8rzlyeWK4KYmJiYmJiYmJiYmJiYmJiYmJiYmJiYmJiYmJiYmJiYmJiYmJiYmJiY2JkFFvmB1wEj3GoAAAAASUVORK5CYII=";

function CastMiniDevPreview() {
  const { castMini } = useGlobalSearchParams<{ castMini?: string }>();
  const insets = useSafeAreaInsets();
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(12)).current;
  const enabled =
    __DEV__ &&
    Platform.OS === "web" &&
    (castMini === "1" || castMini === "fallback");

  useEffect(() => {
    if (!enabled) return;
    const animation = Animated.parallel([
      Animated.timing(opacity, {
        duration: 250,
        easing: Easing.out(Easing.cubic),
        toValue: 1,
        useNativeDriver: true
      }),
      Animated.timing(translateY, {
        duration: 250,
        easing: Easing.out(Easing.cubic),
        toValue: 0,
        useNativeDriver: true
      })
    ]);
    animation.start();
    return () => animation.stop();
  }, [enabled, opacity, translateY]);

  if (!enabled) return null;

  return (
    <View
      style={[
        styles.castMiniHost,
        { bottom: getCastMiniBottomOffset(insets.bottom), pointerEvents: "box-none" }
      ]}
    >
      <Animated.View style={{ opacity, transform: [{ translateY }], width: "100%" }}>
        <CastMiniPill
          artworkUrl={castMini === "fallback" ? null : previewArtworkUrl}
          currentTime={40}
          deviceName="Living Room TV"
          duration={100}
          hasNext
          hasPrevious
          onBody={() => {}}
          onNext={() => {}}
          onPrevious={() => {}}
          onTogglePlay={() => {}}
          itemKey="dev-moonlight-ballet"
          playing
          route="cast"
          title="Moonlight Ballet"
        />
      </Animated.View>
    </View>
  );
}

export function AppProviders({ children }: PropsWithChildren) {
  return (
    <SafeAreaProvider initialMetrics={initialWindowMetrics}>
      <DemoModeProvider>
        <ReadinessProvider>
          <AuthProvider>
            <CatalogProvider>
              <AccountProvider>
                <PlaybackSessionProvider>
                  <AirPlayControllerProvider>
                    <CastControllerProvider>
                      {children}
                      <CastMiniController />
                      <CastMiniDevPreview />
                    </CastControllerProvider>
                  </AirPlayControllerProvider>
                </PlaybackSessionProvider>
              </AccountProvider>
            </CatalogProvider>
          </AuthProvider>
        </ReadinessProvider>
      </DemoModeProvider>
    </SafeAreaProvider>
  );
}

const styles = StyleSheet.create({
  castMiniHost: {
    alignItems: "center",
    left: 0,
    paddingHorizontal: space.md,
    position: "absolute",
    right: 0,
    zIndex: 80
  }
});
